// hooks/invitations/invitations.ts
import {UseMutationResult, UseQueryResult} from "@tanstack/react-query";
// Import Orval-generated API clients
import {
    useCreateInvitation as useCreateInvitationClient,
    useUpdateDetails,
    useValidateOTP as useValidateOTPClient,
    useIsInvitationExists,
    useGetInvitations as useGetInvitationsClient,
    useGetInvitationByPhoneNumber as useGetInvitationByPhoneNumberClient,
    getTransactionById,
    useRequestSelfInvitation,
    useSendPhoneOtp as useSendPhoneOtpClient,
    useVerifyPhoneOtp as useVerifyPhoneOtpClient,
    useProcessInitialDeposit as useProcessInitialDepositClient,
    useAllowedDomains
} from '@/shared/api/client/invitations';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { InvitationDTO } from "@/shared/api/model/invitationDTO";
import { InvitationFilterDTO } from "@/shared/api/model";
import { GetInvitationsParams } from "@/shared/api/model/getInvitationsParams";
import { Pageable } from "@/shared/api/model/pageable";
import { PageInvitationDTO } from "@/shared/api/model/pageInvitationDTO";
import { PushResponseDTO } from "@/shared/api/model/pushResponseDTO";
import { TransactionRecordDTO } from "@/shared/api/model/transactionRecordDTO";
import { SelfInvitationRequestDTO } from "@/shared/api/model/selfInvitationRequestDTO";
import { VerifyOtpRequest } from "@/shared/api/model/verifyOtpRequest";

export const useCreateInvitation = (): UseMutationResult<InvitationDTO, Error, InvitationDTO> => {
    const mutation = useCreateInvitationClient();
    
    return {
        ...mutation,
        data: mutation.data,
        mutateAsync: async (invitationDto: InvitationDTO) => {
            const result = await mutation.mutateAsync({ data: invitationDto });
            return result;
        }
    } as unknown as UseMutationResult<InvitationDTO, Error, InvitationDTO>;
};

export const useUpdateInvitation = (): UseMutationResult<InvitationDTO, Error, InvitationDTO> => {
    const mutation = useUpdateDetails();
    
    return {
        ...mutation,
        data: mutation.data,
        mutateAsync: async (invitationDto: InvitationDTO) => {
            const result = await mutation.mutateAsync({ data: invitationDto });
            return result;
        }
    } as unknown as UseMutationResult<InvitationDTO, Error, InvitationDTO>;
};

export const useValidateOTP = (): UseMutationResult<InvitationDTO, Error, InvitationDTO> => {
    const mutation = useValidateOTPClient();
    
    return {
        ...mutation,
        data: mutation.data,
        mutateAsync: async (invitationDto: InvitationDTO) => {
            const result = await mutation.mutateAsync({ data: invitationDto });
            return result;
        }
    } as unknown as UseMutationResult<InvitationDTO, Error, InvitationDTO>;
};

export const useInvitationExists = (): UseMutationResult<boolean, Error, InvitationDTO> => {
    const mutation = useIsInvitationExists();
    
    return {
        ...mutation,
        data: mutation.data,
        mutateAsync: async (invitationDto: InvitationDTO) => {
            const result = await mutation.mutateAsync({ data: invitationDto });
            return result;
        }
    } as unknown as UseMutationResult<boolean, Error, InvitationDTO>;
};

export const useGetInvitations = (filter: InvitationFilterDTO, pageable: Pageable): UseQueryResult<PageInvitationDTO, Error> => {
    // Create params object expected by the Orval client
    const params: GetInvitationsParams = {
        pageable: pageable
    };
    
    const mutation = useGetInvitationsClient();
    
    return {
        data: mutation.data,
        isLoading: mutation.isPending,
        isError: mutation.isError,
        refetch: async () => {
            const result = await mutation.mutateAsync({
                data: filter,
                params: params
            });
            return { data: result };
        }
    } as unknown as UseQueryResult<PageInvitationDTO, Error>;
};

export function useRegistrationDomains(): UseQueryResult<string[], Error> {
    const result = useAllowedDomains();
    
    return {
        ...result,
        data: result.data
    } as unknown as UseQueryResult<string[], Error>;
}

export const useProcessInitialDeposit = (): UseMutationResult<PushResponseDTO, Error, InvitationDTO> => {
    const mutation = useProcessInitialDepositClient();
    
    return {
        ...mutation,
        data: mutation.data,
        mutateAsync: async (invitationDto: InvitationDTO) => {
            const result = await mutation.mutateAsync({ data: invitationDto });
            return result;
        }
    } as unknown as UseMutationResult<PushResponseDTO, Error, InvitationDTO>;
};

export const useGetInvitationByPhoneNumber = (): UseMutationResult<InvitationDTO, Error, string> => {
    // Convert query to mutation-like interface for backward compatibility
    return {
        mutate: (phoneNumber: string) => {
            useGetInvitationByPhoneNumberClient(phoneNumber);
        },
        mutateAsync: async (phoneNumber: string) => {
            const result = await useGetInvitationByPhoneNumberClient(phoneNumber).refetch();
            return result.data;
        },
        isLoading: false,
        isError: false,
        isSuccess: true,
        reset: () => {},
        data: undefined,
        error: null
    } as unknown as UseMutationResult<InvitationDTO, Error, string>;
};

export const useGetTransactionStatus = (): UseMutationResult<TransactionRecordDTO, Error, string> => {
    // Use the direct API function instead of extracting it from the hook
    // This follows the pattern from the MEMORY about Orval migration
    const queryClient = useQueryClient();
    
    // Convert to mutation-like interface for backward compatibility
    return {
        mutate: (transactionId: string) => {
            // No-op for compatibility
        },
        mutateAsync: async (transactionId: string) => {
            try {
                // Call the API function directly
                const response = await getTransactionById(transactionId);
                return response;
            } catch (error) {
                throw error;
            }
        },
        isLoading: false,
        isError: false,
        isSuccess: true,
        reset: () => {},
        data: undefined,
        error: null
    } as unknown as UseMutationResult<TransactionRecordDTO, Error, string>;
};

export function useSelfInvitation(): UseMutationResult<boolean, Error, SelfInvitationRequestDTO> {
    const mutation = useRequestSelfInvitation();
    
    return {
        ...mutation,
        data: mutation.data,
        mutateAsync: async (request: SelfInvitationRequestDTO) => {
            const result = await mutation.mutateAsync({ data: request });
            return result;
        }
    } as unknown as UseMutationResult<boolean, Error, SelfInvitationRequestDTO>;
}

export function useSendPhoneOtp(): UseMutationResult<VerifyOtpRequest, Error, string> {
    const mutation = useSendPhoneOtpClient();
    
    return {
        ...mutation,
        data: mutation.data,
        mutateAsync: async (phoneNumber: string) => {
            const result = await mutation.mutateAsync({ phoneNumber });
            return result;
        }
    } as unknown as UseMutationResult<VerifyOtpRequest, Error, string>;
}

export function useVerifyPhoneOtp(): UseMutationResult<boolean, Error, VerifyOtpRequest> {
    const mutation = useVerifyPhoneOtpClient();
    
    return {
        ...mutation,
        data: mutation.data,
        mutateAsync: async (request: VerifyOtpRequest) => {
            const result = await mutation.mutateAsync({ data: request });
            return result;
        }
    } as unknown as UseMutationResult<boolean, Error, VerifyOtpRequest>;
}
